import React from 'react'

/**
 * HelpTooltip component for displaying contextual help information.
 *
 * Features:
 * - Shows tooltip on hover (desktop) and tap (mobile)
 * - CSS-only approach for performance
 * - Configurable position (top/bottom/left/right)
 * - Accessible with aria attributes
 * - Dark mode support
 */

interface HelpTooltipProps {
  /** The tooltip content text */
  text: string
  /** Position of the tooltip relative to the icon */
  position?: 'top' | 'bottom' | 'left' | 'right'
}

const positionClasses: Record<string, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
}

const arrowClasses: Record<string, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-200 border-x-transparent border-b-transparent',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 dark:border-b-gray-200 border-x-transparent border-t-transparent',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-200 border-y-transparent border-r-transparent',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-200 border-y-transparent border-l-transparent',
}

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  text,
  position = 'top',
}) => {
  return (
    <span className="relative inline-flex items-center group" role="tooltip-container">
      <button
        type="button"
        className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-help"
        aria-label={`Help: ${text}`}
        tabIndex={0}
      >
        ?
      </button>
      <span
        className={`absolute ${positionClasses[position]} z-50 px-2 py-1 text-xs font-normal text-white dark:text-gray-900 bg-gray-900 dark:bg-gray-200 rounded shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-150 pointer-events-none`}
        role="tooltip"
        aria-hidden="true"
      >
        {text}
        <span
          className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`}
        />
      </span>
    </span>
  )
}
